import { TZ } from '@/models/Timezone';

export enum RelativeDateUnitOfMeasure {
  DAY = 'day',
  HOUR = 'hour',
  MINUTE = 'minute',
  SECOND = 'second',
  CLOSED = 'closed',
}

export const relativeDate = (
  date: Date
): { difference: number; unitOfMeasure: RelativeDateUnitOfMeasure } => {
  if (!date) {
    return { difference: 0, unitOfMeasure: RelativeDateUnitOfMeasure.CLOSED };
  }

  const now = new Date();
  const diff = new Date(date).getTime() - now.getTime();

  if (diff < 0) {
    return { difference: 0, unitOfMeasure: RelativeDateUnitOfMeasure.CLOSED };
  }

  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    return { unitOfMeasure: RelativeDateUnitOfMeasure.DAY, difference: days };
  } else if (hours > 0) {
    return { unitOfMeasure: RelativeDateUnitOfMeasure.HOUR, difference: hours };
  } else if (minutes > 0) {
    return { unitOfMeasure: RelativeDateUnitOfMeasure.MINUTE, difference: minutes };
  } else {
    return { unitOfMeasure: RelativeDateUnitOfMeasure.SECOND, difference: seconds };
  }
};

export const toUTC = (date: Date): Date => {
  const nowUTC = new Date(
    Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      date.getUTCHours(),
      date.getUTCMinutes(),
      date.getUTCSeconds()
    )
  );

  return nowUTC;
};

export const dateIsInDay = (date: Date, day: Date = new Date()): boolean => {
  if (!date || !day) {
    return false;
  }

  return (
    date.getFullYear() === day.getFullYear() &&
    date.getMonth() === day.getMonth() &&
    date.getDate() === day.getDate()
  );
};

const getStartOfMonth = (dateIn: Date = new Date()): Date => {
  const date = new Date(dateIn);
  date.setDate(1);
  return date;
};

const getEndOfMonth = (dateIn: Date = new Date()): Date => {
  const date = new Date(dateIn);
  date.setMonth(date.getMonth() + 1);
  date.setDate(0);
  return date;
};

export const getWeek = (dateIn: Date = new Date()): Date[] => {
  const startOfWeek = new Date(dateIn);
  startOfWeek.setDate(dateIn.getDate() - dateIn.getDay());
  const endOfWeek = new Date(dateIn);
  endOfWeek.setDate(dateIn.getDate() - dateIn.getDay() + 6);

  const dayIndex = new Date(startOfWeek);
  const week = [];

  while (dayIndex <= endOfWeek) {
    week.push(new Date(dayIndex));

    dayIndex.setDate(dayIndex.getDate() + 1);
  }
  return week;
};

export const getWeeks = (dateIn: Date = new Date()): Date[][] => {
  const weeks: Date[][] = [];

  const startOfMonth = getStartOfMonth(dateIn);
  const endOfMonth = getEndOfMonth(dateIn);

  const dayOfMonthStart = startOfMonth.getDay();
  const dayOfMonthEnd = endOfMonth.getDay();

  const startOfCalendarMonth = new Date(dateIn);
  startOfCalendarMonth.setDate(startOfMonth.getDate() - dayOfMonthStart);
  const endOfCalendarMonth = new Date(dateIn);
  endOfCalendarMonth.setDate(endOfMonth.getDate() + (6 - dayOfMonthEnd));

  const dayIndex = new Date(startOfCalendarMonth);
  let weekIndex = 0;
  let week = [];

  while (dayIndex <= endOfCalendarMonth) {
    week.push(new Date(dayIndex));

    dayIndex.setDate(dayIndex.getDate() + 1);
    weekIndex += 1;

    if (weekIndex % 7 === 0) {
      weeks.push(week);
      week = [];
    }
  }

  if (week.length > 0) {
    weeks.push(week);
  }

  return weeks;
};

export const displayUTCDate = (dateIn: Date | undefined) => {
  const localDate = dateIn ? new Date(dateIn) : new Date();
  const date = new Date(localDate.getTime() + localDate.getTimezoneOffset() * 60000);
  return date;
};

export const displayDate = (
  dateIn: Date | undefined,
  language: string,
  isDateOnly: boolean = false,
  tz?: TZ
): string => {
  if (!dateIn) {
    return '-';
  }

  let localization = '';

  switch (language) {
    case 'es':
      localization = 'es-ES';
      break;
    case 'fr':
      localization = 'fr-FR';
      break;
    case 'en':
    default:
      localization = 'en-US';
      break;
  }

  const localDate = new Date(dateIn);

  const date = new Date(
    localDate.getTime()
    //+ localDate.getTimezoneOffset() * 60000
  );

  let parts: any = {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
  };

  if (!isDateOnly) {
    parts = {
      ...parts,
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    };
  }

  if (tz) {
    parts = {
      ...parts,
      timeZone: tz.value,
    };
  }

  return `${date.toLocaleDateString(localization, parts)}`;
};

export const isDueSoon = (dueDate: Date | string | undefined | null): boolean => {
  if (!dueDate) return false;
  try {
    const d = dueDate instanceof Date ? dueDate : new Date(dueDate);
    const diff = d.getTime() - Date.now();
    return diff > 0 && diff < 48 * 60 * 60 * 1000;
  } catch {
    return false;
  }
};
